import React from "react";
import { motion } from "framer-motion";

const categories = [
  {
    id: "admin",
    label: "Administrative & Operational Staff",
    pct: "10–14%",
    color: "#3b82f6",
    hook: "What you pay for the team that still can't keep up",
    bullets: [
      {
        title: "The headcount cost",
        text: "Front desk, office manager, billing coordinator, scheduling — roughly $10K–$14K/mo in fully-loaded payroll on a $100K practice.",
      },
      {
        title: "Hiring & training drag",
        text: "Every new front-desk or biller takes $3K–$6K to recruit and 60–90 days to fully ramp. During that ramp they miscode claims, fumble calls, and drop tasks — the cost shows up as leakage, not as a line item.",
      },
      {
        title: "Churn & reliability",
        text: "Dental front-desk turnover runs 25–40% annually. PTO, sick days, and no-call no-shows mean the desk is understaffed multiple days a month — the phones, verifications, and follow-ups simply don't get done.",
      },
    ],
  },
  {
    id: "leakage",
    label: "Revenue Leakage",
    pct: "10–25%",
    color: "#6366f1",
    hook: "Money earned — but never collected",
    bullets: [
      {
        title: "Missed inbound calls",
        text: "20–35% of business-hour calls go unanswered — 80% of them scheduling-related. Only 14% of new-patient callers leave a voicemail; the rest call the next practice. ≈ $5K–$15K/mo.",
      },
      {
        title: "RCM failures & denials",
        text: "15–20% of claims denied on first pass — 77% from admin errors (names, IDs, attachments). 67% are never resubmitted. ≈ $5K–$10K/mo in earned revenue that never hits the bank.",
      },
      {
        title: "Underpayments & aging AR",
        text: "No one reconciles paid claims vs. contracted rates. Secondary insurance left on the table. AR balloons past 90 days — tens of thousands per year quietly evaporate.",
      },
    ],
  },
  {
    id: "opportunity",
    label: "Missed Opportunity",
    pct: "28–55%",
    color: "#a855f7",
    hook: "Revenue that could exist — but doesn't",
    bullets: [
      {
        title: "Case acceptance",
        text: "Avg practice closes 35–50% of presented treatment; top performers hit 80–85%. A 15–20pt lift on $100K = $15K–$30K/mo — same patients already in the chair.",
      },
      {
        title: "Hygiene recall & retention",
        text: "55–65% recall rate means 35–45% of patients don't come back. Pushing to 80%+ = $50K–$100K/yr — plus downstream restorative from every diagnostic exam.",
      },
      {
        title: "Schedule optimization",
        text: "15–30% no-shows. No real-time waitlist fill. Manual scheduling creates gaps and conflicts that cut daily capacity 10–15%. ≈ $5K–$10K/mo in lost production.",
      },
      {
        title: "Missed growth potential",
        text: "When the office runs itself — clean ops, full schedule, collected AR — you get the bandwidth and peace of mind to actually grow: add a chair, open a second location, invest in marketing. Stuck in firefighting mode, that growth never happens.",
      },
    ],
  },
];

const BandwidthProblemSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-8 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1300px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-6 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 mb-3 tracking-tight leading-[1.1]"
          >
            But humans have limited bandwidth, and this is what ends up{" "}
            <span className="text-blue-600">leading to revenue loss</span>
          </motion.h2>
        </div>

        {/* 3-card grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {categories.map((cat, i) => (
            <motion.div
              key={cat.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 + i * 0.1 }}
              className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.1)] p-6 flex flex-col border-t-[4px]"
              style={{ borderTopColor: cat.color }}
            >
              {/* Header */}
              <div className="flex items-baseline justify-between mb-1 gap-3">
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.18em] tabular-nums"
                  style={{ color: cat.color }}
                >
                  0{i + 1}
                </span>
                <span
                  className="text-[15px] font-extrabold tabular-nums flex-shrink-0"
                  style={{ color: cat.color }}
                >
                  {cat.pct}
                </span>
              </div>
              <h3 className="text-[18px] lg:text-[19px] font-bold text-slate-900 leading-tight mb-1">
                {cat.label}
              </h3>
              <p className="text-[13.5px] text-slate-500 leading-snug mb-4">
                {cat.hook}
              </p>

              {/* Bullets */}
              <div className="flex flex-col gap-4 flex-1">
                {cat.bullets.map((b, j) => (
                  <div key={j}>
                    <h4
                      className="text-[13.5px] font-bold leading-tight mb-1"
                      style={{ color: cat.color }}
                    >
                      {b.title}
                    </h4>
                    <p className="text-[12.5px] text-slate-600 leading-[1.55]">
                      {b.text}
                    </p>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

      </div>
    </div>
  );
};

export default BandwidthProblemSlide;
